import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useParams } from 'react-router';
import { AvatarDisplay, AvatarDisplayType } from '@/components/Avatar/AvatarDisplay/AvatarDisplay';
import { Image, Text } from '@/components/base';
import { AvatarIcon } from '@/components/Revamp/Navbar/AvatarIcon';
import { HomeIcon } from '@/components/Revamp/Navbar/HomeIcon';
import { LeaderboardIcon } from '@/components/Revamp/Navbar/LeaderboardIcon';
import { NotebookIcon } from '@/components/Revamp/Navbar/NotebookIcon';
import { useAuthContext } from '@/contexts/authContext';
import { useAvatarSelection } from '@/hooks/avatar/useAvatarSelection';
import { useGamificationDeterminer } from '@/hooks/featureManagers/useGamificationDeterminer';
import { authenticatedRoutes } from '@/router/routes';
import { toClassNames } from '@/utils';

type Props = {
  className?: string;
};

export enum ActiveButton {
  HOME = 'Home',
  LEADERBOARD = 'Leaderboard',
  PROFILE = 'Profile',
}

export const SideNavbar: FC<Props> = ({ className = '' }) => {
  const { t } = useTranslation('common');

  const { user, logout } = useAuthContext();
  const { isGamification } = useGamificationDeterminer();

  const { data: avatarSelection } = useAvatarSelection();

  const currentAvatarUrl = avatarSelection?.avatarItem?.imageUrl;
  const currentDecorationUrl = avatarSelection?.headAccessoryItem?.equippedImageUrl;
  const currentBackgroundUrl = avatarSelection?.backgroundItem?.imageUrl;

  const { classroomId } = useParams();
  const queryParams = useMemo(() => {
    const params = new URLSearchParams();
    if (classroomId) {
      params.set('classroomId', classroomId);
    }
    return params;
  }, [classroomId]);

  const userName = useMemo(() => {
    if (user?.firstname && user?.lastname) {
      return `${user.firstname} ${user.lastname}`;
    } else if (user?.firstname) {
      return user.firstname;
    }
    return t('nav_bar.profile');
  }, [user, t]);

  return (
    <div
      className={toClassNames(
        'flex h-full w-[134px] flex-col justify-between bg-revamp-cobalt-500 px-8 pb-4 pt-6',
        className
      )}
    >
      <Image src="/assets/images/icon/logo.svg" alt="logo" />
      <div className="flex flex-col gap-2.5">
        <NavLink
          className="flex w-full flex-col justify-center gap-2 p-2 text-center hover:cursor-pointer"
          to={authenticatedRoutes.Classrooms.getRoute()}
        >
          {({ isActive }) => (
            <>
              <div className="flex items-center justify-center">
                <HomeIcon className={isActive ? 'h-10 w-10 text-white' : 'h-10 w-10 text-revamp-blue-400'} />
              </div>
              <Text
                className={toClassNames(
                  'whitespace-nowrap font-semibold leading-[18px]',
                  isActive ? 'text-white' : 'text-revamp-blue-400'
                )}
              >
                {t('nav_bar.home')}
              </Text>
            </>
          )}
        </NavLink>
        <NavLink
          className="flex w-full flex-col items-center justify-center gap-2 p-2 text-center hover:cursor-pointer"
          to={authenticatedRoutes.LeaderboardPodium.getRoute({ query: queryParams.toString() })}
        >
          {({ isActive }) => (
            <>
              <div className="flex items-center justify-center">
                <LeaderboardIcon className={isActive ? 'h-10 w-10 text-white' : 'h-10 w-10 text-revamp-blue-400'} />
              </div>
              <Text
                className={toClassNames(
                  'whitespace-nowrap font-semibold leading-[18px]',
                  isActive ? 'text-white' : 'text-revamp-blue-400'
                )}
              >
                {t('nav_bar.leaderboard')}
              </Text>
            </>
          )}
        </NavLink>
        <NavLink
          className="flex w-full flex-col items-center justify-center gap-2 p-2 text-center hover:cursor-pointer"
          to={authenticatedRoutes.UserNotebook.getRoute()}
        >
          {({ isActive }) => (
            <>
              <div className="flex items-center justify-center">
                <NotebookIcon className={isActive ? 'h-10 w-10 text-white' : 'h-10 w-10 text-revamp-blue-400'} />
              </div>
              <Text
                className={toClassNames(
                  'whitespace-nowrap font-semibold leading-[18px]',
                  isActive ? 'text-white' : 'text-revamp-blue-400'
                )}
              >
                {t('nav_bar.notebook')}
              </Text>
            </>
          )}
        </NavLink>
        {/** Navigate Profile to be implement */}
        <NavLink
          className="flex w-full flex-col items-center justify-center gap-2 p-2 text-center hover:cursor-pointer"
          to={authenticatedRoutes.Profile.getRoute()}
        >
          {({ isActive }) => (
            <>
              <div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-full">
                {isGamification ? (
                  <AvatarDisplay
                    variant={AvatarDisplayType.Default}
                    avatarImageUrl={currentAvatarUrl}
                    decorationImageUrl={currentDecorationUrl}
                    backgroundImageUrl={currentBackgroundUrl}
                  />
                ) : (
                  <AvatarIcon
                    className="h-10 w-10 text-[#A3D9F6]"
                    bgClassName={toClassNames('h-10 w-10', isActive ? 'text-white' : 'text-revamp-blue-400')}
                  />
                )}
              </div>
              <Text
                className={toClassNames(
                  'max-w-20 overflow-hidden text-ellipsis whitespace-nowrap font-semibold leading-[18px]',
                  isActive ? 'text-white' : 'text-revamp-blue-400'
                )}
              >
                {userName}
              </Text>
            </>
          )}
        </NavLink>
      </div>
      <div className="flex flex-col items-center gap-2 p-2 hover:cursor-pointer" onClick={() => logout?.()}>
        <Image src="/assets/images/icon/exit.svg" alt="logo" />
        <Text className="whitespace-nowrap font-semibold leading-[18px] text-revamp-blue-400">
          {t('nav_bar.logout')}
        </Text>
      </div>
    </div>
  );
};
